<template>
  <b-container
    fluid
    style="max-width: 1200px"
  >
    <!-- Header row -->
    <b-row style="margin-top: 4em">
      <b-col>
        <h2 class="text-secondary m-1">
          {{ $t("globals.all_customers") + " " }}
          <v-icon v-if="getAllCustomersLoading" name="spinner" spin></v-icon>
        </h2>
      </b-col>
      <b-col>
        <b-btn
          class="float-right m-1"
          size='sm'
          @click="csvExportModal.show = true"
          v-b-tooltip.hover
          :title=" $t('globals.export')"
        > 
          {{ $t('globals.export') }}
          <v-icon class='ml-2' name="file-download"></v-icon>
        </b-btn>
      </b-col>
    </b-row>
    <!-- Search row -->
    <b-row class="my-2">
      <!-- Search name -->
      <b-col style="min-width:250px;max-width: 300px;">
        <b-input-group size="sm" class="m-1">
          <b-input-group-prepend is-text>
            {{ $t("globals.name") }}
          </b-input-group-prepend>
          <b-form-input
            v-model="searchQuery.name"
            @keyup.enter="searchCustomersLikeName"
            :disabled="getAllCustomersLoading"
          />
          <b-input-group-append>
            <b-btn
              variant="info"
              :disabled="getAllCustomersLoading"
              @click="searchCustomersLikeName"
            >
              <v-icon name="search"></v-icon>
            </b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-col>
      <!-- Search web app id -->
      <b-col style="min-width:250px;max-width: 300px;">
        <b-input-group size="sm" class="m-1">
          <b-input-group-prepend is-text>
            {{ "WebApp ID" }}
          </b-input-group-prepend>
          <b-form-input
            v-model="searchQuery.webAppId"
            @keyup.enter="searchCustomersWebAppId"
            :disabled="getAllCustomersLoading"
          />
          <b-input-group-append>
            <b-btn
              variant="info"
              :disabled="getAllCustomersLoading"
              @click="searchCustomersWebAppId"
            >
              <v-icon name="search"></v-icon>
            </b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-col>
      <!-- Search debiteur no. -->
      <b-col style="min-width:250px;max-width: 300px;">      
        <b-input-group size="sm" class="m-1">
          <b-input-group-prepend is-text>
            {{ "Debiteur no." }}
          </b-input-group-prepend>
          <b-form-input
            v-model="searchQuery.debiteurNo"
            @keyup.enter="searchCustomersDebiteurNo"
            :disabled="getAllCustomersLoading"
          />
          <b-input-group-append>
            <b-btn
              variant="info"
              :disabled="getAllCustomersLoading"
              @click="searchCustomersDebiteurNo"
            >
              <v-icon name="search"></v-icon>
            </b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-col>
      <!-- Clear/Reload -->
      <b-col style="min-width: 130px">
        <b-btn
          variant="danger"
          size="sm"
          class="m-1 float-right"
          @click="resetReload()"
        >
          {{ $t("globals.clear") + " / " + $t("globals.reload") }}
        </b-btn>
      </b-col>
    </b-row>
    <!-- Table row -->
    <b-row class="my-2">
      <b-col>
        <b-table
          class='m-1'
          hover small
          primary-key="id"
          :per-page="0"
          :items="getAllCustomers"
          :fields="fields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          @row-clicked="rowClicked"
          @sort-changed="sortingChanged"
        >
        </b-table>
      </b-col>
    </b-row>
    <!-- Pagination row -->
    <simple-pagination-row
      :show='paginationEnabled'
      :loading='getAllCustomersLoading'
      :offset='offset'
      :fetchLess='fetchLess'
      :fetchMore='fetchMore'
    />
    <!-- Modals -->
    <export-csv-modal :show="csvExportModal.show" :close="closeModals"/>
  </b-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  components: {
    "export-csv-modal": () =>
      import("@/components/customers/all_customers/ExportCsvModal.vue"),
    'simple-pagination-row': () =>
      import('@/components/common/SimplePaginationRow'),
  },
  data() {
    return {
      csvExportModal: {
        show: false
      },
      searchQuery: {
        name: null,
        webAppId: null,
        debiteurNo: null
      },
      offset: 0,
      limit: 20,
      sortBy: "created_at",
      sortDesc: true,
      currentPage: 1,
      fields: [
        {
          key: "id",
          sortable: true
        },
        {
          key: "company_name",
          label: this.$t("globals.name"),
          sortable: true
        },
        {
          key: "no_devices",
          label: this.$t("globals.number_of_devices")
        },
        {
          key: "web_app_id",
          sortable: true
        },
        {
          key: "debiteur_no",
          label: this.$t("globals.debiteur_no"),
          sortable: true
        }
      ]
    };
  },
  computed: {
    ...mapGetters("customers", ["getAllCustomers", "getAllCustomersLoading"]),
    paginationEnabled() {
      return (
        (this.searchQuery.name == null || this.searchQuery.name == '')
        && (this.searchQuery.webAppId == null || this.searchQuery.webAppId == '')
        && (this.searchQuery.debiteurNo == null ||this.searchQuery.debiteurNo == '')
      )
    }
  },
  methods: {
    ...mapActions("customers", [
      "fetchCustomersPagination",
      "fetchCustomersLikeName",
      "fetchCustomersDebiteurNo",
      "fetchCustomersWebAppId"
    ]),
    rowClicked(item) {
      this.$router.push({ name: "customer", params: { id: item.id } });
    },
    fetchMore() {
      this.offset += 20;
      this.fetch();
    },
    fetchLess() {
      if (this.offset >= 20) {
        this.offset -= 20;
        this.fetch();
      }
    },
    resetOffsetSort() {
      this.offset = 0;
      this.sortBy = "created_at";
      this.sortDesc = true;
    },
    resetQuery() {
      this.searchQuery.name = null;
      this.searchQuery.webAppId = null;
      this.searchQuery.debiteurNo = null;
    },
    fetch() {
      let order_by = this.sortBy;
      if (this.sortDesc) {
        order_by += " DESC";
      } else {
        order_by += " ASC";
      }

      this.fetchCustomersPagination({
        this_ref: this,
        limit: this.limit,
        offset: this.offset,
        order: order_by
      });
    },
    searchCustomersLikeName() {
      if (this.searchQuery.name != null && this.searchQuery.name.length > 2) {
        this.searchQuery.webAppId = null;
        this.searchQuery.debiteurNo = null;

        this.resetOffsetSort();
        this.fetchCustomersLikeName({name: this.searchQuery.name, this_ref: this,});
      }
    },
    searchCustomersDebiteurNo() {
      if (this.searchQuery.debiteurNo != null) {
        this.searchQuery.webAppId = null;
        this.searchQuery.name = null;

        this.resetOffsetSort();
        this.fetchCustomersDebiteurNo({debiteurNo: this.searchQuery.debiteurNo, this_ref: this,});
      }
    },
    searchCustomersWebAppId() {
      if (this.searchQuery.webAppId != null) {
        this.searchQuery.debiteurNo = null;
        this.searchQuery.name = null;

        this.resetOffsetSort();
        this.fetchCustomersWebAppId({webAppId: this.searchQuery.webAppId, this_ref: this,});
      }
    },
    closeModals() {
      this.csvExportModal.show = false;
      this.resetReload();
    },
    sortingChanged(ctx) {
      // Field key for sorting by (or null for no sorting)
      this.sortBy = ctx.sortBy || "created_at";
      // true if sorting descending, false otherwise
      this.sortDesc = ctx.sortDesc
      
      this.fetch();
    },
    resetReload() {
      this.resetOffsetSort();
      this.resetQuery();
      this.fetch();
    }
  },
  mounted() {
    this.resetReload();
  }
};
</script>
